/**
 * VÉRAULT Portal — Client · Experience Proposal (Phase 2 · Pass 1)
 *
 * Renders the published proposal microsite. Sections are read from
 * `trip_proposals` via /api/design-studio/{trip_id}/proposal. In preview
 * mode (advisor-side) the advisor token grants access. In live client
 * mode, the linked-client guard enforces access via Suite Door rules.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Sparkles, Calendar, MapPin, Home, Utensils, Plane, DollarSign, Compass, Send } from "lucide-react";
import { Surface, api } from "../../../index";
import SmartConfirmations from "../../../components/SmartConfirmations";
import ProposalDecision from "../../../components/ProposalDecision";
import { HotelCard, BudgetPlanningRange } from "../../../components/ProposalEnrichedCards";
import { ProposalCommentsToggle } from "../../../components/ProposalComments";
import SafeImage, { isAdvisorPreviewMode } from "../../../components/SafeImage";
import TripShapeCard, { buildTripShapeRows } from "../../../components/TripShapeCard";
import ProposalItemCard from "./ProposalItemCard";
import FlightJourneyCard from "./FlightJourneyCard";
import DecisionCenter from "./DecisionCenter";
import ConfirmationsProgress from "../../../components/ConfirmationsProgress";
import OutstandingActionsCard from "../../../components/OutstandingActionsCard";
import ExportMenu from "../../../components/ExportMenu";



const MARKDOWN_COMPONENTS = {
  h2: (p) => <h2 className="mt-6 font-serif text-[22px] sm:text-[24px] leading-tight text-[hsl(var(--foreground))]" {...p} />,
  h3: (p) => <h3 className="mt-5 font-serif text-[18px] sm:text-[19px] leading-tight text-[hsl(var(--foreground))]" {...p} />,
  p:  (p) => <p className="my-3" {...p} />,
  ul: (p) => <ul className="my-3 list-disc list-inside space-y-1.5" {...p} />,
  ol: (p) => <ol className="my-3 list-decimal list-inside space-y-1.5" {...p} />,
  li: (p) => <li className="font-serif text-[15px] sm:text-[16px] leading-relaxed" {...p} />,
  blockquote: (p) => (
    <blockquote
      className="my-6 pl-5 border-l-2 border-[hsl(var(--accent-gold))]/60 font-accent italic text-[16px] sm:text-[17px] text-[hsl(var(--foreground-secondary))] leading-relaxed"
      {...p}
    />
  ),
  hr: () => <hr className="my-6 border-0 h-px bg-[rgba(0,0,0,0.08)]" />,
  strong: (p) => <strong className="font-serif text-[hsl(var(--foreground))]" {...p} />,
  em: (p) => <em className="font-accent" {...p} />,
};

const SECTION_ICONS = {
  opening: Sparkles,
  destination_direction: MapPin,
  flights: Plane,
  recommended_stay: Home,
  experiences: Compass,
  dining: Utensils,
  logistics_flow: Plane,
  investment: DollarSign,
  alternate_direction: Compass,
  next_steps: Send,
};

export default function ClientProposal({ tripId: tripIdProp }) {
  const params = useParams();
  const tripId = tripIdProp || params.tripId;
  const [proposal, setProposal] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [loadState, setLoadState] = React.useState("loading"); // loading | ready | not-published | error

  // In advisor preview the URL lives under `/portal/advisor/`. Everywhere
  // else this is the live client suite — use the Suite-Door endpoint.
  const isAdvisorPreview =
    typeof window !== "undefined" && (window.location.pathname || "").includes("/portal/advisor/");

  React.useEffect(() => {
    let alive = true;
    setLoadState("loading");
    const proposalPath = isAdvisorPreview
      ? `/design-studio/${tripId}/proposal`
      : `/client-portal/trips/${tripId}/proposal`;
    api
      .get(proposalPath)
      .then(({ data }) => {
        if (!alive) return;
        if (data?.status !== "published" && data?.status !== "approved") {
          setLoadState("not-published");
          return;
        }
        setProposal(data);
        // load items referenced by the proposal so we can render their copy.
        // Advisor preview can use the design-studio items endpoint; live
        // clients receive `items` inline on the proposal payload if the
        // backend chooses to denormalize. For now, only fetch when in preview.
        if (isAdvisorPreview) {
          api
            .get(`/design-studio/${tripId}/items`)
            .then(({ data: list }) => { if (alive) setItems(Array.isArray(list) ? list : []); })
            .catch(() => alive && setItems([]));
        } else {
          api
            .get(`/client-portal/trips/${tripId}/design-studio-items`)
            .then(({ data: list }) => { if (alive) setItems(Array.isArray(list) ? list : []); })
            .catch(() => alive && setItems([]));
        }
        setLoadState("ready");
      })
      .catch(() => { if (alive) setLoadState("error"); });
    return () => { alive = false; };
  }, [tripId, isAdvisorPreview]);

  if (loadState === "loading") {
    return (
      <div className="py-20 text-center" data-testid="client-proposal-loading">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Curating your proposal…</p>
      </div>
    );
  }

  if (loadState === "not-published") {
    return (
      <div className="py-20 text-center max-w-md mx-auto" data-testid="client-proposal-pending">
        <p className="font-serif text-[20px] text-[hsl(var(--foreground))]">Your proposal is being shaped.</p>
        <p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
          Your advisor is finalizing the details. You&apos;ll be notified the moment it&apos;s ready to read.
        </p>
      </div>
    );
  }

  if (loadState === "error" || !proposal) {
    return (
      <div className="py-20 text-center" data-testid="client-proposal-error">
        <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
          We couldn&apos;t open the proposal right now. Please return to your suite or contact your advisor.
        </p>
      </div>
    );
  }

  const sections = proposal.sections || {};
  // VÉRAULT correction pass — final editorial section order. `next_steps`
  // is intentionally omitted here; the closing "A Moment to Reflect" +
  // Submit Confirmations rendered by <SmartConfirmations> covers it.
  const visibleSections = ["opening", "destination_direction", "flights", "recommended_stay", "experiences", "dining", "logistics_flow", "investment", "alternate_direction"]
    .map((key) => ({ key, ...(sections[key] || {}) }))
    .filter((s) => s.enabled && ((s.body && s.body.trim()) || (s.item_ids && s.item_ids.length) || (Array.isArray(s.journeys) && s.journeys.length)));

  return (
    <article className="max-w-4xl mx-auto space-y-16 sm:space-y-20" data-testid="client-proposal">
      {/* Unified confirmation model — counter + "Still needed from you" */}
      <div className="!mb-[-2rem] space-y-3">
        <ConfirmationsProgress
          endpoint={`/client-portal/trips/${tripId}/confirmations-summary`}
          compact
          testIdBase="client-proposal-confirmations"
        />
        <OutstandingActionsCard
          endpoint={`/client-portal/trips/${tripId}/confirmations-summary`}
          testIdBase="client-outstanding-actions"
        />
      </div>
      {/* Feb 2026 — single Export control top-right. Understated, never
          competing with the main client CTA. Dropdown: Preview · Download
          · Print. PDF mirrors the same shared records as this page. */}
      <div className="flex items-center justify-end mb-[-3rem]" data-testid="client-proposal-toolbar">
        <ExportMenu
          label="Export"
          testid="client-proposal-export"
          downloadUrl={`${process.env.REACT_APP_BACKEND_URL}/api/proposals/${tripId}/proposal.pdf`}
          downloadFilename={`verault-proposal-${tripId}.pdf`}
          previewUrl={`${process.env.REACT_APP_BACKEND_URL}/api/proposals/${tripId}/proposal.pdf`}
        />
      </div>
      {(() => {
        const advisorMode = isAdvisorPreviewMode();
        if (!proposal.cover_image_url && !advisorMode) return null;
        return (
          <div className="relative -mx-4 sm:-mx-6 lg:-mx-12 -mt-6 mb-2" data-testid="client-proposal-cover">
            <div className="w-full overflow-hidden">
              <SafeImage
                src={proposal.cover_image_url}
                alt=""
                aspect="aspect-[21/9] sm:aspect-[16/6]"
                mode={advisorMode ? "advisor" : "client"}
                placeholderLabel="Cover image pending"
                testId="client-proposal-cover-image"
              />
            </div>
            {proposal.cover_image_credit_name && proposal.cover_image_url && (
              <p className="absolute bottom-2 right-3 font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/80">
                © {proposal.cover_image_credit_name}
              </p>
            )}
          </div>
        );
      })()}
      {/* Hero — editorial header */}
      <header className="text-center space-y-4 pb-8 mb-2 border-b border-[rgba(0,0,0,0.06)]">
        <p
          className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--accent-gold))]"
          data-testid="client-proposal-eyebrow"
        >
          Experience Proposal
        </p>
        <h1 className="font-serif text-[34px] sm:text-[42px] lg:text-[48px] leading-[1.05] text-[hsl(var(--foreground))] max-w-2xl mx-auto">
          {proposal.headline || "Your VÉRAULT experience."}
        </h1>
        {proposal.prepared_for && (
          <p
            className="font-serif italic text-[16px] sm:text-[17px] text-[hsl(var(--foreground-secondary))]"
            data-testid="client-proposal-prepared-for"
          >
            Prepared for {proposal.prepared_for}
          </p>
        )}
        {proposal.subline && (
          <p
            className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] max-w-xl mx-auto"
            data-testid="client-proposal-subline"
          >
            {proposal.subline}
          </p>
        )}
      </header>

      {/* Phase 5 — modular client decisions, organized calmly by module */}
      <DecisionCenter tripId={tripId} readOnly={isAdvisorPreview} />

      {visibleSections.length === 0 ? (
        <p className="text-center font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
          The first details are being added now. Check back shortly.
        </p>
      ) : (
        visibleSections.map((s, idx) => {
          const Icon = SECTION_ICONS[s.key] || Sparkles;
          const linked = (s.item_ids || []).map((id) => items.find((it) => it.id === id)).filter(Boolean);
          const isDestinationDirection = s.key === "destination_direction";
          const tripShapeRows = isDestinationDirection
            ? buildTripShapeRows({ proposal, items, trip: proposal?.trip })
            : null;
          const useTripShape = isDestinationDirection && tripShapeRows && tripShapeRows.length >= 2;
          return (
            <motion.section
              key={s.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              className="space-y-5"
              id={`section-${s.key}`}
              data-testid={`client-proposal-section-${s.key}`}
            >
              <div className="flex items-center gap-2 text-[hsl(var(--accent-gold))]">
                <Icon className="size-3.5" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.26em]">{titleFor(s.key)}</span>
              </div>
              {/* Section title — used as Portugal Stay / Greece Stay etc. */}
              <h2 className="font-serif text-[26px] sm:text-[30px] leading-tight text-[hsl(var(--foreground))]">
                {titleFor(s.key)}
              </h2>
              {/* Wave 1 · Priority 2 — Trip Shape replaces dense Destination Direction prose */}
              {useTripShape && (
                <TripShapeCard
                  shape={{ headline: s.body ? null : null, rows: tripShapeRows }}
                  testIdBase={`client-tripshape`}
                />
              )}
              {/* Original body — kept when not replaced by structured component.
                  Wave 3 · Priority 1 — render markdown so advisor formatting
                  toolbar (## heading, **bold**, lists, > pull quotes, --- dividers)
                  produces real editorial layout client-side. */}
              {s.body && !useTripShape && (
                <div className="font-serif text-[16px] sm:text-[17px] leading-[1.75] text-[hsl(var(--foreground))] proposal-markdown-body max-w-3xl">
                  <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                    {s.body}
                  </ReactMarkdown>
                </div>
              )}
              {/* Design Studio Redesign · Phase 1 — Flight Studio journey cards */}
              {s.key === "flights" && Array.isArray(s.journeys) && s.journeys.length > 0 && (
                <div className="space-y-6 mt-3" data-testid="client-proposal-flight-journeys">
                  {s.journeys.map((jn) => (
                    <FlightJourneyCard key={jn.id} journey={jn} />
                  ))}
                  <p
                    className="font-accent italic text-[12px] leading-relaxed text-[hsl(var(--foreground-muted))] text-center"
                    data-testid="client-proposal-flights-disclaimer"
                  >
                    Flight schedules, availability, and pricing are subject to change until tickets have been booked and confirmed.
                  </p>
                </div>
              )}
              {/* PI-3 · Pass 2 P1-A — investment section uses the structured planning-range panel */}
              {s.key === "investment" && <BudgetPlanningRange investment={proposal?.investment} />}
              {/* PI-3 · Pass 2 P1-A — recommended_stay renders enriched hotel cards.
                  Feb 2026 QA — per-item anchored Needed Confirmations: a question
                  whose `anchor_item_id` matches a hotel renders directly after
                  that hotel card (e.g. Wedding Hotel after Sheraton Cascais). */}
              {s.key === "recommended_stay" && linked.length > 0 && (
                <div className="space-y-6 mt-3">
                  {linked
                    .filter((it) => it.category === "hotel")
                    .map((it) => (
                      <React.Fragment key={it.id}>
                        <HotelCard item={it} />
                        <SmartConfirmations
                          tripId={tripId}
                          filterToSection={s.key}
                          filterToAnchor={it.id}
                          hideEmpty
                        />
                      </React.Fragment>
                    ))}
                </div>
              )}
              {/* Fallback compact item grid for every other section.
                  Feb 2026 — replaced the old plain-text Surface with
                  ProposalItemCard so editorial structure (markdown
                  client_copy, detail rows like Timing / Best for / Pace
                  / Address) carries through to the published proposal. */}
              {s.key !== "recommended_stay" && linked.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                  {linked.map((it) => (
                    <ProposalItemCard key={it.id} item={it} />
                  ))}
                </div>
              )}
              {/* Wave 1 · Priority 4 — confirmations woven inline next to their section.
                  Per-item anchored questions are excluded here (rendered next to their
                  specific item card in recommended_stay above). */}
              <SmartConfirmations
                tripId={tripId}
                filterToSection={s.key}
                excludeAnchored
                hideEmpty
              />
              {/* PI-3 · P1 backlog · slice a — per-section client comment toggle */}
              <ProposalCommentsToggle
                tripId={tripId}
                targetKind="section"
                targetId={s.key}
                targetLabel={titleFor(s.key)}
                testIdBase={`client-proposal-comments-${s.key}`}
              />
            </motion.section>
          );
        })
      )}

      <SmartConfirmations tripId={tripId} />

      {/* Feb 2026 client-readiness pass — Approve / Request Changes */}
      <ProposalDecision tripId={tripId} />
    </article>
  );
}

function titleFor(key) {
  return {
    opening: "Opening · Trip Vision",
    destination_direction: "At a Glance",
    flights: "Flights",
    recommended_stay: "Recommended Stays",
    experiences: "Signature Moments",
    dining: "Dining",
    logistics_flow: "Logistics · Flow",
    investment: "Investment",
    alternate_direction: "Alternate Direction",
    next_steps: "A Moment to Reflect",
  }[key] || key;
}
